/* global axios */
/* eslint-disable max-classes-per-file */
import ApiClient from './ApiClient';

class LegalProcessesAPI extends ApiClient {
  constructor() {
    super('legal-processes', { accountScoped: true });
  }

  getAll({ contactId, status, q, page = 1 } = {}) {
    return axios.get(this.url, {
      params: { contact_id: contactId, status, q, page },
    });
  }

  // Arquivar/desarquivar a PASTA (= este processo). POST em ação própria, e
  // não PATCH de status: tem autor, data, motivo e cria o andamento final.
  // O backend exige administrador E senha.
  //
  // Arquiva SÓ esta pasta — a 300/1 do mesmo caso continua correndo, que foi
  // a correção da Dra. Jullya em 2026-08-08.
  archive(id, { reason, archived_at: archivedAt, current_password: password }) {
    return axios.post(`${this.url}/${id}/archive`, {
      reason,
      archived_at: archivedAt,
      current_password: password,
    });
  }

  unarchive(id, { reason, current_password: password }) {
    return axios.post(`${this.url}/${id}/unarchive`, {
      reason,
      current_password: password,
    });
  }

  getById(id) {
    return axios.get(`${this.url}/${id}`);
  }

  create(data) {
    return axios.post(this.url, { legal_process: data });
  }

  update(id, data) {
    return axios.patch(`${this.url}/${id}`, { legal_process: data });
  }

  delete(id) {
    return axios.delete(`${this.url}/${id}`);
  }

  getStats() {
    return axios.get(`${this.url}/stats`);
  }

  getDashboard(period) {
    return axios.get(`${this.url}/dashboard`, {
      params: period ? { period } : {},
    });
  }

  // `extra` aceita `contact_id` para restringir a busca às pastas de um cliente
  // — usado pelo Financeiro, onde oferecer qualquer pasta da conta convida a
  // vincular a cobrança de um cliente à pasta de outro.
  search(query, extra = {}) {
    return axios.get(`${this.url}/search`, { params: { q: query, ...extra } });
  }

  reports({ from, to } = {}) {
    return axios.get(`${this.url}/reports`, { params: { from, to } });
  }

  validateCnj(numeroCnj) {
    return axios.get(`${this.url}/validate_cnj`, {
      params: { numero_processo: numeroCnj },
      timeout: 10000,
    });
  }

  // `opcoes` carrega o `signal` de quem quer poder desistir: cada tecla
  // digitada cancela a busca anterior em vez de deixar nove consultas correndo
  // no banco ao mesmo tempo (medido em 18/09/2026, tela de Processos).
  globalSearch(q, opcoes = {}) {
    const { limit = 5, signal } =
      typeof opcoes === 'number' ? { limit: opcoes } : opcoes;
    return axios.get(`${this.url}/global_search`, {
      params: { q, limit },
      timeout: 5000,
      signal,
    });
  }

  analyzePdf(formData) {
    return axios.post(`${this.url}/analyze_pdf`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 90000,
    });
  }

  // Processos do escritório que ainda não estão no advup (07/10/2026):
  // `{ fonte: 'djen', oab, uf }` ou `{ fonte: 'tramitacao' }`. Só descobre;
  // quem cria é o bulkImport. O DJEN de 12 meses leva alguns segundos.
  descobrir(params) {
    return axios.get(`${this.url}/descobrir`, { params, timeout: 120000 });
  }

  bulkImport(cnjNumbers) {
    return axios.post(
      `${this.url}/bulk_import`,
      { cnj_numbers: cnjNumbers },
      { timeout: 120000 }
    );
  }

  getTasks(processId, params = {}) {
    return axios.get(`${this.url}/${processId}/tasks`, { params });
  }

  createTask(processId, data) {
    return axios.post(`${this.url}/${processId}/tasks`, { legal_task: data });
  }

  getMovements(processId, params = {}) {
    return axios.get(`${this.url}/${processId}/movements`, {
      params: { per_page: 50, ...params },
    });
  }

  createMovement(processId, data) {
    return axios.post(`${this.url}/${processId}/movements`, {
      legal_movement: data,
    });
  }

  deleteMovement(processId, movementId) {
    return axios.delete(`${this.url}/${processId}/movements/${movementId}`);
  }

  triggerFetchMovements(processId) {
    return axios.post(`${this.url}/${processId}/movements/fetch`);
  }

  getOfficeSetting() {
    return axios.get(
      this.url.replace('legal-processes', 'legal-office-setting')
    );
  }

  /**
   * @param {object} data campos do escritório
   * @param {string} [blobId] logo recém-enviada ao Active Storage. String
   *   vazia remove a logo atual; `undefined` não mexe nela — a tela precisa
   *   distinguir "tirei o logo" de "não toquei no logo".
   */
  updateOfficeSetting(data, blobId) {
    const corpo = { legal_office_setting: data };
    if (blobId !== undefined) corpo.blob_id = blobId;
    return axios.patch(
      this.url.replace('legal-processes', 'legal-office-setting'),
      corpo
    );
  }

  getDocuments(processId) {
    return axios.get(`${this.url}/${processId}/documents`);
  }

  /**
   * @param {(porcentagem: number) => void} [aoProgredir] chamado enquanto o
   *   arquivo sobe, com 0–100. Existe porque o envio em lote mostra uma barra
   *   por arquivo — sem isto um arquivo de 50 MB ficaria parado por um minuto
   *   sem sinal de vida.
   */
  createDocument(processId, formData, aoProgredir) {
    return axios.post(`${this.url}/${processId}/documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: evento => {
        if (!aoProgredir || !evento.total) return;
        aoProgredir(Math.round((evento.loaded * 100) / evento.total));
      },
    });
  }

  // A senha do Meu INSS do cliente, sob demanda: ela NÃO vem no payload da
  // ficha, que é carregado a cada abertura e fica em cache do navegador.
  getSenhaInss(processId) {
    return axios.get(`${this.url}/${processId}/senha-inss`);
  }

  getAudits(processId) {
    return axios.get(`${this.url}/${processId}/audits`);
  }

  // Resumo de processo por IA (BuscaProcessos) — fluxo assíncrono.
  // Docs: https://docs.buscaprocessos.app.br/docs/resumo-ia
  solicitarResumoIa(processId) {
    return axios.post(
      `${this.url}/${processId}/resumo-ia/solicitar-atualizacao`
    );
  }

  getResumoIaStatus(processId, requestId) {
    return axios.get(`${this.url}/${processId}/resumo-ia/status`, {
      params: { request_id: requestId },
    });
  }

  getResumoIa(processId) {
    return axios.get(`${this.url}/${processId}/resumo-ia`);
  }

  getFees(processId) {
    return axios.get(`${this.url}/${processId}/fees`);
  }

  createFee(processId, data) {
    return axios.post(`${this.url}/${processId}/fees`, data);
  }

  // Fase 1 do roadmap de paridade Legal One — motor de contingência
  // (ver .claude/specs/in-progress/paridade-legalone-processo-enriquecido.md)
  getClaims(processId) {
    return axios.get(`${this.url}/${processId}/claims`);
  }

  createClaim(processId, data) {
    return axios.post(`${this.url}/${processId}/claims`, data);
  }

  createClaimClassification(processId, claimId, data) {
    return axios.post(
      `${this.url}/${processId}/claims/${claimId}/classifications`,
      data
    );
  }

  // Sub-fase 2.2 do roadmap de paridade Legal One — Envolvidos (ver
  // .claude/specs/plans/paridade-legalone-processo-fase2-visual-estrutural.md)
  getInvolveds(processId) {
    return axios.get(`${this.url}/${processId}/involveds`);
  }

  createInvolved(processId, data) {
    return axios.post(`${this.url}/${processId}/involveds`, data);
  }

  updateInvolved(processId, id, data) {
    const base = this.url.replace('legal-processes', 'legal-process-involveds');
    return axios.patch(`${base}/${id}`, data);
  }

  deleteInvolved(processId, id) {
    const base = this.url.replace('legal-processes', 'legal-process-involveds');
    return axios.delete(`${base}/${id}`);
  }

  // Parceiros (escritório/advogado parceiro do processo) — mesmo padrão de
  // Envolvidos, mas vinculando um Partner (Configurações > Compromissos >
  // Parceiros) em vez de um Contato.
  getPartners(processId) {
    return axios.get(`${this.url}/${processId}/partners`);
  }

  createPartner(processId, data) {
    return axios.post(`${this.url}/${processId}/partners`, data);
  }

  updatePartner(processId, id, data) {
    const base = this.url.replace('legal-processes', 'legal-process-partners');
    return axios.patch(`${base}/${id}`, data);
  }

  deletePartner(processId, id) {
    const base = this.url.replace('legal-processes', 'legal-process-partners');
    return axios.delete(`${base}/${id}`);
  }

  // Sub-fase 2.3 do roadmap de paridade Legal One — Vínculos (ver
  // .claude/specs/plans/paridade-legalone-processo-fase2-visual-estrutural.md)
  getProcessLinks(processId) {
    return axios.get(`${this.url}/${processId}/process_links`);
  }

  createProcessLink(processId, data) {
    return axios.post(`${this.url}/${processId}/process_links`, data);
  }

  updateProcessLink(processId, id, data) {
    const base = this.url.replace('legal-processes', 'legal-process-links');
    return axios.patch(`${base}/${id}`, data);
  }

  deleteProcessLink(processId, id) {
    const base = this.url.replace('legal-processes', 'legal-process-links');
    return axios.delete(`${base}/${id}`);
  }

  // Sub-fase 2.5 do roadmap de paridade Legal One — Gastos (ver
  // .claude/specs/plans/paridade-legalone-processo-fase2-visual-estrutural.md)
  getExpenses(processId) {
    return axios.get(`${this.url}/${processId}/expenses`);
  }

  createExpense(processId, data) {
    return axios.post(`${this.url}/${processId}/expenses`, data);
  }

  updateExpense(processId, id, data) {
    const base = this.url.replace('legal-processes', 'legal-process-expenses');
    return axios.patch(`${base}/${id}`, data);
  }

  deleteExpense(processId, id) {
    const base = this.url.replace('legal-processes', 'legal-process-expenses');
    return axios.delete(`${base}/${id}`);
  }

  // Sub-fase 2.6 do roadmap de paridade Legal One — Objetos (ver
  // .claude/specs/plans/paridade-legalone-processo-fase2-visual-estrutural.md)
  getProcessObjects(processId) {
    return axios.get(`${this.url}/${processId}/process_objects`);
  }

  createProcessObject(processId, data) {
    return axios.post(`${this.url}/${processId}/process_objects`, data);
  }

  updateProcessObject(processId, id, data) {
    const base = this.url.replace('legal-processes', 'legal-process-objects');
    return axios.patch(`${base}/${id}`, data);
  }

  deleteProcessObject(processId, id) {
    const base = this.url.replace('legal-processes', 'legal-process-objects');
    return axios.delete(`${base}/${id}`);
  }

  // Sub-fase 2.7 do roadmap de paridade Legal One — Garantias e depósitos
  // (ver .claude/specs/plans/paridade-legalone-processo-fase2-visual-estrutural.md)
  getGuarantees(processId) {
    return axios.get(`${this.url}/${processId}/guarantees`);
  }

  createGuarantee(processId, data) {
    return axios.post(`${this.url}/${processId}/guarantees`, data);
  }

  updateGuarantee(processId, id, data) {
    const base = this.url.replace(
      'legal-processes',
      'legal-process-guarantees'
    );
    return axios.patch(`${base}/${id}`, data);
  }

  deleteGuarantee(processId, id) {
    const base = this.url.replace(
      'legal-processes',
      'legal-process-guarantees'
    );
    return axios.delete(`${base}/${id}`);
  }
}

class LegalTasksAPI extends ApiClient {
  constructor() {
    super('legal-tasks', { accountScoped: true });
  }

  getAll({ contactId, status, dueSoon } = {}) {
    return axios.get(this.url, {
      params: { contact_id: contactId, status, due_soon: dueSoon },
    });
  }

  update(id, data) {
    return axios.patch(`${this.url}/${id}`, { legal_task: data });
  }

  delete(id) {
    return axios.delete(`${this.url}/${id}`);
  }
}

class LegalDocumentsAPI extends ApiClient {
  constructor() {
    super('legal-documents', { accountScoped: true });
  }

  delete(id) {
    return axios.delete(`${this.url}/${id}`);
  }
}

class LegalFeesAPI extends ApiClient {
  constructor() {
    super('legal-fees', { accountScoped: true });
  }

  update(id, data) {
    return axios.patch(`${this.url}/${id}`, data);
  }

  delete(id) {
    return axios.delete(`${this.url}/${id}`);
  }
}

class LegalProcessClaimsAPI extends ApiClient {
  constructor() {
    super('legal-process-claims', { accountScoped: true });
  }

  update(id, data) {
    return axios.patch(`${this.url}/${id}`, data);
  }

  delete(id) {
    return axios.delete(`${this.url}/${id}`);
  }
}

class LegalProcessClaimClassificationsAPI extends ApiClient {
  constructor() {
    super('legal-process-claim-classifications', { accountScoped: true });
  }

  update(id, data) {
    return axios.patch(`${this.url}/${id}`, data);
  }

  delete(id) {
    return axios.delete(`${this.url}/${id}`);
  }
}

class LegalIntimationsAPI extends ApiClient {
  constructor() {
    super('legal-intimations', { accountScoped: true });
  }

  getAll({ oab, status } = {}) {
    return axios.get(this.url, { params: { oab, status } });
  }

  getById(id) {
    return axios.get(`${this.url}/${id}`);
  }

  markAsRead(id) {
    return axios.post(`${this.url}/${id}/mark_as_read`);
  }

  sync(desde) {
    return axios.post(`${this.url}/sync`, null, {
      params: desde ? { desde } : {},
    });
  }

  activateMonitoring() {
    return axios.post(
      `${this.url.replace(
        'legal-intimations',
        'legal-office-setting'
      )}/activate_intimacoes_monitoring`
    );
  }
}

export const LegalProcessesApi = new LegalProcessesAPI();
export const LegalIntimationsApi = new LegalIntimationsAPI();
export const LegalTasksApi = new LegalTasksAPI();
export const LegalDocumentsApi = new LegalDocumentsAPI();
export const LegalFeesApi = new LegalFeesAPI();
export const LegalProcessClaimsApi = new LegalProcessClaimsAPI();
export const LegalProcessClaimClassificationsApi =
  new LegalProcessClaimClassificationsAPI();
